import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { CENARIOS_PADRAO, CRITERIOS_PADRAO, type Criterios } from '@profitlens/calculo';
import { db } from '../config/firebase';
import type { CriteriosSalvos, CustoFixoSalvo, PerfilCusto } from '../tipos';

/**
 * Perfis de custo e critérios de classificação.
 *
 * É aqui que a pendência P2 do planejamento se resolve: alíquota, comissão e
 * tarifa dependem do regime tributário e da categoria do anúncio, e o sistema
 * não tem como saber nenhum dos dois. Enquanto não forem preenchidos, a análise
 * sai como DADOS_INSUFICIENTES — que é o resultado correto, não uma falha.
 */

/** O documento é único: um conjunto de critérios para a operação toda. */
export const ID_CRITERIOS = 'padrao';

export type RascunhoPerfil = Omit<
  PerfilCusto,
  'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

/**
 * Perfil novo nasce com os percentuais em NULO, de propósito.
 *
 * Preencher com zero seria fingir que a operação não paga comissão nem imposto,
 * e o lucro sairia inflado com cara de número conferido. Vazio força a decisão
 * a passar por alguém.
 */
export function perfilVazio(): RascunhoPerfil {
  return {
    nome: '',
    padrao: false,
    // Verde é a tabela sem acréscimo. Quem estiver em outra cor troca aqui — e
    // o custo do frete sobe junto, que é o efeito real da reputação.
    reputacaoML: 'VERDE',
    impostoVendaBps: null,
    devolucaoBps: null,
    custoFinanceiroBps: null,
    freteSubsidiadoCents: 0,
    custosVendaFixosCents: 0,
    custosVendaAntecipadosCents: 0,
    perdaBps: 0,
    observacoes: null,
  };
}

export function usePerfisCusto() {
  const [perfis, setPerfis] = useState<PerfilCusto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'perfisCusto'), orderBy('nome')),
        (s) => {
          setPerfis(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PerfilCusto));
          setCarregando(false);
        },
        (e) => {
          setErro(e.message);
          setCarregando(false);
        },
      ),
    [],
  );

  return { perfis, carregando, erro };
}

/**
 * Grava o perfil garantindo que exista NO MÁXIMO UM padrão.
 *
 * A transação é o que fecha a brecha: marcar dois perfis como padrão em abas
 * diferentes deixaria a análise escolhendo um deles por sorte da ordenação — e
 * o mesmo produto daria resultados diferentes sem ninguém ter mudado nada.
 */
export async function salvarPerfilCusto(
  id: string | null,
  dados: RascunhoPerfil,
  usuarioId: string,
): Promise<string> {
  const ref = id ? doc(db, 'perfisCusto', id) : doc(collection(db, 'perfisCusto'));

  await runTransaction(db, async (tx) => {
    let outros: { id: string }[] = [];
    if (dados.padrao) {
      const { getDocs } = await import('firebase/firestore');
      const todos = await getDocs(collection(db, 'perfisCusto'));
      outros = todos.docs.filter((d) => d.id !== ref.id && d.data().padrao === true);
    }

    if (id) {
      tx.update(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
    } else {
      tx.set(ref, {
        ...dados,
        criadoEm: serverTimestamp(),
        criadoPor: usuarioId,
        atualizadoEm: null,
        atualizadoPor: null,
      });
    }

    for (const o of outros) {
      tx.update(doc(db, 'perfisCusto', o.id), { padrao: false });
    }
  });

  return ref.id;
}

export async function removerPerfilCusto(id: string): Promise<void> {
  await deleteDoc(doc(db, 'perfisCusto', id));
}

/**
 * Quais campos obrigatórios ainda estão em branco. Vazio = perfil utilizável.
 *
 * Comissão e tarifa fixa saíram desta lista: elas não se cadastram, se
 * consultam no Mercado Livre pelo preço exato da análise. O perfil ficou só com
 * o que é fato sobre a operação de quem usa.
 */
export function faltandoNoPerfil(p: RascunhoPerfil): string[] {
  const obrigatorios: [keyof RascunhoPerfil, string][] = [
    ['impostoVendaBps', 'imposto sobre a venda'],
    ['devolucaoBps', 'taxa de devolução'],
    ['custoFinanceiroBps', 'custo financeiro'],
  ];
  return obrigatorios.filter(([c]) => p[c] === null).map(([, rotulo]) => rotulo);
}

// ---------- critérios ----------

export type RascunhoCriterios = Omit<
  CriteriosSalvos,
  'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

/**
 * Os critérios têm padrão de partida, ao contrário dos custos.
 *
 * A diferença não é descuido: "margem mínima de 15%" é uma POLÍTICA, um ponto
 * de partida razoável que cada um ajusta. Já "comissão do marketplace" é um
 * FATO sobre a operação de quem usa — inventar um valor ali seria inventar
 * dinheiro.
 */
export function criteriosPadrao(): RascunhoCriterios {
  // O volume esperado nasce VAZIO: e premissa de quem opera, e um numero
  // inventado aqui dilui custo fixo com um denominador que ninguem escolheu.
  return { ...CRITERIOS_PADRAO, ...CENARIOS_PADRAO, vendasMensaisEsperadas: null };
}

export function useCriterios() {
  const [criterios, setCriterios] = useState<CriteriosSalvos | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(
    () =>
      onSnapshot(
        doc(db, 'criterios', ID_CRITERIOS),
        (d) => {
          setCriterios(d.exists() ? ({ id: d.id, ...d.data() } as CriteriosSalvos) : null);
          setCarregando(false);
        },
        () => setCarregando(false),
      ),
    [],
  );

  return { criterios, carregando };
}

export async function salvarCriterios(
  dados: RascunhoCriterios,
  usuarioId: string,
  jaExistia: boolean,
): Promise<void> {
  const ref = doc(db, 'criterios', ID_CRITERIOS);
  if (jaExistia) {
    await updateDoc(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
  } else {
    await setDoc(ref, {
      ...dados,
      criadoEm: serverTimestamp(),
      criadoPor: usuarioId,
      atualizadoEm: null,
      atualizadoPor: null,
    });
  }
}

/** Os critérios que a análise deve usar: os salvos, ou os de partida. */
export function criteriosEmVigor(salvos: CriteriosSalvos | null): Criterios {
  return salvos ? salvos : CRITERIOS_PADRAO;
}

// ---------- custos fixos ----------

export type RascunhoCustoFixo = Omit<
  CustoFixoSalvo,
  'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

export const custoFixoVazio = (): RascunhoCustoFixo => ({
  nome: '',
  valorMensalCents: 0,
  ativo: true,
  observacoes: null,
});

export function useCustosFixos() {
  const [custos, setCustos] = useState<CustoFixoSalvo[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'custosFixos'), orderBy('nome')),
        (s) => {
          setCustos(s.docs.map((d) => ({ id: d.id, ...d.data() }) as CustoFixoSalvo));
          setCarregando(false);
        },
        () => setCarregando(false),
      ),
    [],
  );

  return { custos, carregando };
}

export async function salvarCustoFixo(
  id: string | null,
  dados: RascunhoCustoFixo,
  usuarioId: string,
): Promise<void> {
  const ref = id ? doc(db, 'custosFixos', id) : doc(collection(db, 'custosFixos'));
  if (id) {
    await updateDoc(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
  } else {
    await setDoc(ref, {
      ...dados,
      criadoEm: serverTimestamp(),
      criadoPor: usuarioId,
      atualizadoEm: null,
      atualizadoPor: null,
    });
  }
}

export const removerCustoFixo = (id: string) => deleteDoc(doc(db, 'custosFixos', id));
